// src/components/GroupTab.jsx
import React, { useState, useEffect } from 'react';
import { Alert, Row, Col, Badge, ListGroup, Spinner, Button, Modal } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, documentId } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import {supabaseDataService} from "@/services/supabaseService.js";

const GroupTab = ({ onSuccess, onError }) => {
    const { user, removeUserFromGroup, isGroupAdmin } = useAuth();

    // Group-related state
    const [groupMembers, setGroupMembers] = useState([]);
    const [groupInfo, setGroupInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [userToRemove, setUserToRemove] = useState(null);
    const [removeLoading, setRemoveLoading] = useState(false);

    useEffect(() => {
        if (user?.groups?.length > 0) {
            loadGroupInfo();
        }
    }, [user?.groups]);

    useEffect(() => {
        if (error && onError) {
            onError(error);
        }
    }, [error, onError]);

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';

        try {
            // Handle Firestore timestamp
            if (timestamp.seconds) {
                return new Date(timestamp.seconds * 1000).toLocaleDateString();
            }
            // Handle regular date
            return new Date(timestamp).toLocaleDateString();
        } catch (error) {
            return 'N/A';
        }
    };

    const loadGroupInfo = async () => {
        if (!user?.groups?.length) return;

        setLoading(true);
        setError('');

        try {
            const primaryGroup = user.groupName; // Use the first group

            const groupMembers = await supabaseDataService.getGroupMembers(user.groupData[0].group_id);
            const uids = [];
            groupMembers.forEach((member) => {
                uids.push(member.uid);
            });

            const usersQuery = query(
                collection(db, 'users'),
                where(documentId(), 'in', uids)
            );
            const usersSnapshot = await getDocs(usersQuery);

            const members = [];
            usersSnapshot.forEach((doc) => {
                const userData = doc.data();
                members.push({
                    id: doc.id,
                    email: userData.email,
                    firstName: userData.firstName || '',
                    lastName: userData.lastName || '',
                    displayName: userData.firstName ?
                        `${userData.firstName} ${userData.lastName || ''}`.trim() :
                        userData.email?.split('@')[0] || 'User',
                    isGroupAdmin: userData.isGroupAdmin || false,
                    createdAt: userData.createdAt,
                    profileComplete: userData.profileComplete || false
                });
            });

            // Sort members: admins first, then by name
            members.sort((a, b) => {
                if (a.isGroupAdmin && !b.isGroupAdmin) return -1;
                if (!a.isGroupAdmin && b.isGroupAdmin) return 1;
                return a.displayName.localeCompare(b.displayName);
            });

            setGroupMembers(members);

        } catch (error) {
            console.error('Error loading group information:', error);
            const errorMsg = 'Failed to load group information';
            setError(errorMsg);
            if (onError) onError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Handle remove user button click
    const handleRemoveUserClick = (member) => {
        setUserToRemove(member);
        setShowRemoveModal(true);
    };

    // Handle remove user confirmation
    const handleRemoveUserConfirm = async () => {
        if (!userToRemove || !user?.groups?.length) return;

        setRemoveLoading(true);
        setError('');

        try {
            const primaryGroup = user.groups[0];
            await removeUserFromGroup(userToRemove.id, primaryGroup);

            // Remove the user from local state
            setGroupMembers(prev => prev.filter(member => member.id !== userToRemove.id));

            const successMsg = `${userToRemove.displayName} has been removed from the group successfully.`;
            if (onSuccess) onSuccess(successMsg);

            alert('remember to add the remove all the db records');

        } catch (error) {
            console.error('Error removing user:', error);
            const errorMsg = 'Failed to remove user: ' + error.message;
            setError(errorMsg);
            if (onError) onError(errorMsg);
        } finally {
            setRemoveLoading(false);
            setShowRemoveModal(false);
            setUserToRemove(null);
        }
    };

    // Handle remove user modal cancel
    const handleRemoveUserCancel = () => {
        setShowRemoveModal(false);
        setUserToRemove(null);
    };

    if (!user) {
        return <div>Please log in to view group information.</div>;
    }

    return (
        <>
            {error && <Alert variant="danger">{error}</Alert>}

            {/* Group Information */}
            <div className="mb-4">

                {loading ? (
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading group information...
                    </div>
                ) : (
                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <strong>Group Name:</strong> {user.groups[0]}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <strong>Total Members:</strong> {groupMembers.length}
                            </div>
                        </Col>
                        {groupInfo && (
                            <>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Created:</strong> {formatDate(groupInfo.createdAt)}
                                    </div>
                                </Col>
                                {groupInfo.description && (
                                    <Col md={12}>
                                        <div className="mb-3">
                                            <strong>Description:</strong> {groupInfo.description}
                                        </div>
                                    </Col>
                                )}
                            </>
                        )}
                    </Row>
                )}
            </div>

            {/* Group Members */}
            <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 text-primary">Group Members</h5>
                    {isGroupAdmin() && (
                        <Badge bg="primary">
                            You have admin privileges
                        </Badge>
                    )}
                </div>

                {loading ? (
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading members...
                    </div>
                ) : groupMembers.length > 0 ? (
                    <ListGroup>
                        {groupMembers.map((member) => (
                            <ListGroup.Item
                                key={member.id}
                                className="d-flex justify-content-between align-items-center"
                            >
                                <div className="flex-grow-1">
                                    <div className="d-flex align-items-center mb-1">
                                        <strong className="me-2">{member.displayName}</strong>
                                        {member.isGroupAdmin && (
                                            <Badge bg="primary" className="me-2">Admin</Badge>
                                        )}
                                        {member.id === user.uid && (
                                            <Badge bg="secondary">You</Badge>
                                        )}
                                    </div>
                                    <div>
                                        <small className="text-muted d-block">{member.email}</small>
                                        {member.createdAt && (
                                            <small className="text-muted">
                                                Joined: {formatDate(member.createdAt)}
                                            </small>
                                        )}
                                    </div>
                                </div>

                                {/* Admin Controls */}
                                <div className="text-end">
                                    {/* Show remove button only for admins and not for themselves or other admins */}
                                    {isGroupAdmin() && member.id !== user.uid && !member.isGroupAdmin && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleRemoveUserClick(member)}
                                            disabled={removeLoading}
                                            title={`Remove ${member.displayName} from group`}
                                        >
                                            Remove
                                        </Button>
                                    )}

                                    {/* Show note for non-removable users */}
                                    {isGroupAdmin() && member.id !== user.uid && member.isGroupAdmin && (
                                        <small className="text-muted">Cannot remove admin</small>
                                    )}

                                    {isGroupAdmin() && member.id === user.uid && (
                                        <small className="text-muted">Cannot remove yourself</small>
                                    )}
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                ) : (
                    <Alert variant="info">
                        No members found in your group.
                    </Alert>
                )}
            </div>

            {/* Admin Information */}
            {/*{isGroupAdmin() && (*/}
            {/*    <Alert variant="info" className="mb-4">*/}
            {/*        <strong>Admin Privileges:</strong> As a group admin, you can remove regular members from the group.*/}
            {/*        You cannot remove other admins or yourself.*/}
            {/*    </Alert>*/}
            {/*)}*/}

            {/* Remove User Confirmation Modal */}
            <Modal show={showRemoveModal} onHide={handleRemoveUserCancel} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Remove Group Member</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to remove <strong>{userToRemove?.displayName}</strong> from the group?
                    </p>
                    <div className="bg-light p-3 rounded mb-3">
                        <small className="text-muted">
                            <strong>Member Details:</strong><br />
                            Name: {userToRemove?.displayName}<br />
                            Email: {userToRemove?.email}<br />
                            Joined: {formatDate(userToRemove?.createdAt)}
                        </small>
                    </div>
                    <Alert variant="warning" className="mb-0">
                        <small>
                            <strong>Warning:</strong> This action cannot be undone. The user will lose access to all group resources
                            and will need to be re-invited to rejoin the group.
                        </small>
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleRemoveUserCancel}
                        disabled={removeLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleRemoveUserConfirm}
                        disabled={removeLoading}
                    >
                        {removeLoading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Removing...
                            </>
                        ) : (
                            'Remove User'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default GroupTab;